import yup from "yup";
import {
  CustomerAOESettingsInput,
  CustomerEditor_CustomerFragment,
  CustomerInput,
  CustomerType,
  OrganizationType,
  RecruitingPortalType,
  Workshift
} from "@graphql";
import { pick, omit, regexps } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { FormErrors } from "@modules/formx";
import { Address } from "@bonliva-crm/domain/types";

export type FormData = Pick<
  CustomerInput,
  | "Address"
  | "CustomerGroups"
  | "Description"
  | "Name"
  | "OrganizationNumber"
  | "OrganizationType"
  | "Professions"
  | "Type"
  | "WorkshiftSpans"
  | "RecruitingPortalType"
  | "journalSystem"
  | "municipalityId"
  | "bemloOrgId"
  | "bemloUnitId"
> & {
  AOESettings: Array<
    CustomerInput["AOESettings"][0] & {
      Error?: boolean;
    }
  >;
  Hospital: { label: string; value: string };
  Region: { label: string; value: string };
};

const workshiftSpanSchema = yup.object({
  Profession: yup.string().required(),
  Workshift: yup.string().required(),
  FromMinutes: yup.number().required(),
  ToMinutes: yup.number().required()
}) as yup.ObjectSchema<FormData["WorkshiftSpans"][0]>;

export const schema = yup.object<FormData>({
  Address: yup.object({
    City: yup.string(),
    Country: yup.string().required(locale.SelectSomething(locale.Country)),
    Region: yup.string().required(locale.SelectSomething(locale.Region)),
    StreetName: yup.string(),
    ZipCode: yup.string().matches(regexps.zipCodeOrEmpty, "Wrong zip code")
  }),
  Professions: yup.array(),
  AOESettings: yup.array(),
  CustomerGroups: yup.array(),
  Description: yup.string(),
  Hospital: yup.mixed().nullable(true), // todo: validation..
  Name: yup.string().required(`${locale.Name} *`),
  OrganizationNumber: yup.string(), //  matches(organizationNumberOrEmpty, "Wrong org number"),
  OrganizationType: yup
    .string()
    .required(`${locale.OrganizationType} *`)
    .nullable(true) as yup.Schema<OrganizationType>,
  municipalityId: yup.string().when(["Type", "OrganizationType", "Address"], {
    is: (customerType: CustomerType, orgType: OrganizationType, address: Address) => {
      if (address.Country === "Denmark" || address.Country === "Finland") {
        return false;
      }

      if (customerType === CustomerType.Region && orgType === OrganizationType.Commune) {
        return true;
      }

      if (customerType === CustomerType.Hospital && orgType === OrganizationType.County) {
        return true;
      }

      return false;
    },
    then: yup.string().nullable(true).required(locale.Municipality),
    otherwise: yup.string().nullable(true)
  }),
  Region: yup.mixed().nullable(true), // todo: validation..
  Type: yup.string().oneOf(Object.values(CustomerType)) as yup.Schema<CustomerType>,
  WorkshiftSpans: yup.array(workshiftSpanSchema),
  journalSystem: yup.string().nullable(true)
  // RecruitingPortalType: yup
  //   .string()
  //   .required(`${locale.RecruitingPortalType} *`)
  //   .nullable(true) as yup.Schema<RecruitingPortalType>
});

export function customValidate(data: FormData) {
  const errors: FormErrors<FormData> = {};

  if (data.Address?.Country === "Sweden" && data.OrganizationNumber) {
    if (!regexps.seOrgNum.test(data.OrganizationNumber)) {
      errors.OrganizationNumber = "123456-1234";
    }
  }

  if (data.Address?.Country === "Norway" && data.OrganizationNumber) {
    if (!regexps.noOrgNum.test(data.OrganizationNumber)) {
      errors.OrganizationNumber = "123 123 123";
    }
  }

  return errors;
}

export const initialValues: FormData = {
  Address: {
    City: "",
    Country: "",
    Region: "",
    StreetName: "",
    ZipCode: ""
  },
  AOESettings: [],
  CustomerGroups: [],
  Description: "",
  Hospital: null,
  Name: "",
  OrganizationNumber: "",
  OrganizationType: null,
  Professions: [],
  Region: null,
  Type: CustomerType.Region,
  WorkshiftSpans: [],
  RecruitingPortalType: null as RecruitingPortalType,
  journalSystem: null,
  bemloOrgId: "",
  bemloUnitId: ""
};

export const aoeSettingsInitialValues = (
  prof: string,
  aoe: string
): CustomerAOESettingsInput & { Error: boolean } => ({
  AreaOfExpertise: aoe,
  Profession: prof,
  Enabled: false,
  Error: false,
  FortnoxId: "",
  InvoicingData: "",
  orderId: "",
  timeReport: null,
  ResponsiblePerson: null
});

export function fragmentToForm(fragment: CustomerEditor_CustomerFragment): FormData {
  const region = fragment.Parents.find((p) => p.Type === CustomerType.Region);
  const hospital = fragment.Parents.find((p) => p.Type === CustomerType.Hospital);

  return {
    ...omit(
      fragment,
      "_id",
      "AreasOfExpertise",
      "DirectChildren",
      "Parents",
      "OwnProfessions"
    ),
    AOESettings: fragment.AOESettings.map((s) => ({
      ...pick(
        s,
        "Enabled",
        "Error",
        "AreaOfExpertise",
        "Profession",
        "InvoicingData",
        "FortnoxId",
        "GroupInvoicing",
        "orderId",
        "timeReport"
      ),
      ResponsiblePerson: s.ResponsiblePerson
        ? {
            label: s.ResponsiblePerson.Name,
            value: s.ResponsiblePerson.PersonId
          }
        : null
    })),
    Region: region ? { label: region.Name, value: region.Id } : null,
    Hospital: hospital ? { label: hospital.Name, value: hospital.Id } : null
  };
}

export function formToGql(data: FormData): CustomerInput {
  return {
    ...omit(data, "OrganizationType", "Region", "Hospital", "RecruitingPortalType"),
    AOESettings: data.AOESettings.filter(
      (x) => x.GroupInvoicing || (x.Enabled && data.Professions.includes(x.Profession))
    ).map((x) => omit(x, "Error")),
    ParentId: data.Hospital ? data.Hospital.value : data.Region ? data.Region.value : "",
    OrganizationType: data.OrganizationType || null,
    RecruitingPortalType: data.RecruitingPortalType || null
  };
}

export const defaultSpans = {
  [Workshift.Day]: {
    FromMinutes: 8 * 60, // 8 AM
    ToMinutes: 14 * 60
  },
  [Workshift.Evening]: {
    FromMinutes: 14 * 60,
    ToMinutes: 20 * 60
  },
  [Workshift.Night]: {
    FromMinutes: 20 * 60,
    ToMinutes: 2 * 60
  }
};
